import { useEffect, useMemo } from 'react';
import type { ReplaySession } from '../store/replaySessionStore';
import { useReplaySessionStore } from '../store/replaySessionStore';
import { buildReplayGrpcHarDisplayFromSession } from '../utils/replayOpenRequest.utils';
import { isGrpcHarEntry } from '../utils/harGrpcDisplay.utils';
import {
  fetchReplayResponseWebGrpcDecode,
  hasUsableResponseWebGrpcDecode,
} from '../utils/replayGrpcDisplay.utils';

export function useReplayGrpcResponseDecode({
  sessionId,
  session,
  response,
  responseIndex,
}: {
  sessionId: string;
  session: ReplaySession | undefined;
  response: ReplaySession['responseHistory'][number] | null | undefined;
  responseIndex: number;
}) {
  const updateResponseWebGrpcDecodedData = useReplaySessionStore(
    (state) => state.updateResponseWebGrpcDecodedData,
  );
  const grpcHarDisplay = useMemo(() => {
    if (!session) {
      return undefined;
    }

    const candidate =
      session.grpcHarDisplay ??
      buildReplayGrpcHarDisplayFromSession({
        session,
        currentResponse: response ?? undefined,
      });

    if (!candidate || !isGrpcHarEntry(candidate)) {
      return undefined;
    }

    return candidate;
  }, [session, response]);

  useEffect(() => {
    if (
      !grpcHarDisplay ||
      !session?.applicationId ||
      !response ||
      response.error ||
      responseIndex < 0
    ) {
      return;
    }

    if (hasUsableResponseWebGrpcDecode(response.webGrpcDecodedData)) {
      return;
    }

    let cancelled = false;

    void fetchReplayResponseWebGrpcDecode({
      applicationId: session.applicationId,
      grpcHarDisplay,
      sessionRequest: session.request,
      requestData: response.requestData,
      sentRequestWire: response.sentRequestWire ?? session.request.body ?? '',
      sentRequestWireEncoding:
        response.sentRequestWireEncoding ?? session.request.bodyEncoding,
      response: {
        statusCode: response.statusCode,
        statusMessage: response.statusMessage,
        headers: response.headers,
        body: response.body,
        httpVersion: response.httpVersion,
      },
    })
      .then((webGrpcDecodedData) => {
        if (cancelled || !webGrpcDecodedData) {
          return;
        }

        updateResponseWebGrpcDecodedData({
          sessionId,
          responseIndex,
          webGrpcDecodedData,
        });
      })
      .catch((error: unknown) => {
        console.warn('Failed to decode replay gRPC response', {
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    response,
    response?.requestData,
    response?.sentRequestWire,
    response?.sentRequestWireEncoding,
    responseIndex,
    session?.applicationId,
    session?.request,
    grpcHarDisplay,
    sessionId,
    updateResponseWebGrpcDecodedData,
  ]);
}
